import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { IdentityUserSelectorComponent } from '../../../shared/design-system/identity-user-selector.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import {
  SEARCH_OPS_AUTONOMIES,
  SEARCH_OPS_CADENCES,
  SEARCH_OPS_COVERAGE_MODES,
  SearchCoworkerDto,
  SearchCoworkerRunDto,
  SearchCoworkerScheduleDto,
  SearchPropertyListItemDto
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-coworkers',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    IdentityUserSelectorComponent,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    InputNumberModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    TableModule,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.coworkers.title')"
      [description]="t('growth.searchops.coworkers.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.coworkers.help.title'"
        [descriptionKey]="'growth.searchops.coworkers.help.description'"
        [bulletKeys]="[
          'growth.searchops.coworkers.help.bullets.owner',
          'growth.searchops.coworkers.help.bullets.play',
          'growth.searchops.coworkers.help.bullets.approval'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.backlog.actions.workHome')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/backlog']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.coworkers.actions.backlog')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/keywords']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.keywords')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.fields.property') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.coworkers.propertyHelp.title'"
              [descriptionKey]="'growth.searchops.coworkers.propertyHelp.description'" />
          </div>
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              (ngModelChange)="onPropertyChange($event)"
              appendTo="body" />
          </ain-form-field>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.coworkers.sections.list') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.coworkers.listHelp.title'"
              [descriptionKey]="'growth.searchops.coworkers.listHelp.description'" />
          </div>
          <button pButton type="button" [label]="t('growth.searchops.coworkers.actions.create')" (click)="openCreate()"></button>
          @if (coworkers().length === 0) {
            <ain-state type="empty" [title]="t('growth.searchops.coworkers.empty')" />
          } @else {
            <p-table [value]="coworkers()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.searchops.fields.name') }}</th>
                  <th>{{ t('growth.searchops.fields.owner') }}</th>
                  <th>{{ t('growth.searchops.fields.coverageMode') }}</th>
                  <th>{{ t('growth.searchops.fields.status') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><button type="button" class="link" (click)="selectCoworker(row)">{{ row.name }}</button></td>
                  <td><ain-identity-user-selector [ngModel]="row.ownerUserId" [disabled]="true" /></td>
                  <td>{{ t('growth.searchops.coverageMode.' + row.coverageMode) }}</td>
                  <td>{{ t('growth.searchops.coworkerStatus.' + row.status) }}</td>
                  <td class="actions">
                    @if (row.status === 'active') {
                      <button pButton type="button" size="small" severity="secondary" [label]="t('growth.searchops.coworkers.actions.pause')" (click)="pause(row)"></button>
                    } @else {
                      <button pButton type="button" size="small" severity="secondary" [label]="t('growth.searchops.coworkers.actions.resume')" (click)="resume(row)"></button>
                    }
                    <button pButton type="button" size="small" [label]="t('growth.searchops.coworkers.actions.runNow')" (click)="runNow(row)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        @if (selected(); as coworker) {
          <section class="card">
            <div class="section-title">
              <h3>{{ coworker.name }}</h3>
              <ain-info-hint
                [titleKey]="'growth.searchops.coworkers.detailHelp.title'"
                [descriptionKey]="'growth.searchops.coworkers.detailHelp.description'" />
            </div>
            <p class="summary">
              {{ t('growth.searchops.fields.play') }}:
              {{ t('growth.searchops.play.sprint.plan') }} ·
              {{ t('growth.searchops.play.anomaly.digest') }} ·
              {{ t('growth.searchops.play.report.draft') }}
            </p>
            @if (schedule(); as schedule) {
              <p class="summary">
                {{ t('growth.searchops.cadence.' + schedule.cadence) }} · {{ schedule.localTime }} ·
                {{ t('growth.searchops.fields.nextRun') }}: {{ schedule.nextRunAtUtc }}
              </p>
            }
            <h4>{{ t('growth.searchops.coworkers.sections.runs') }}</h4>
            @if (runs().length === 0) {
              <ain-state type="empty" [title]="t('growth.searchops.coworkers.runs.empty')" />
            } @else {
              <p-table [value]="runs()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.searchops.fields.periodKey') }}</th>
                    <th>{{ t('growth.searchops.fields.status') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.periodKey }}</td>
                    <td>{{ t('growth.searchops.runStatus.' + row.status) }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          </section>
        }
      }
    </ain-page>

    <p-dialog
      [header]="t('growth.searchops.coworkers.dialog.create')"
      [(visible)]="createOpen"
      [modal]="true"
      [style]="{ width: '36rem' }"
      appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.coworkers.formHelp.title'"
        [descriptionKey]="'growth.searchops.coworkers.formHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.name')">
        <input pInputText [(ngModel)]="createModel.name" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.owner')">
        <ain-identity-user-selector [(ngModel)]="createModel.ownerUserId" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.positions')">
        <p-multiSelect
          [options]="positionOptions()"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="createModel.positionCodes"
          appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.coverageMode')">
        <p-select [options]="coverageOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.coverageMode" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.autonomy')">
        <p-select [options]="autonomyOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.autonomy" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.cadence')">
        <p-select [options]="cadenceOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.cadence" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.localTime')">
        <input pInputText [(ngModel)]="createModel.localTime" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.weekday')">
        <p-inputNumber [(ngModel)]="createModel.weekday" [min]="1" [max]="7" />
      </ain-form-field>
      <label class="check-row">
        <p-checkbox [(ngModel)]="createModel.killSwitch" [binary]="true" />
        <span>{{ t('growth.searchops.fields.killSwitch') }}</span>
      </label>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createOpen = false"></button>
        <button pButton type="button" [label]="t('common.save')" (click)="saveCoworker()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
    .card { background: var(--ain-color-surface, #fff); padding: 16px; border-radius: 12px; margin-bottom: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .section-title h3 { margin: 0; font-size: 1rem; }
    .live-badge, .summary { font-size: 0.85rem; color: #64748b; }
    .actions { display: flex; flex-wrap: wrap; gap: 6px; }
    .link { background: none; border: 0; color: var(--ain-color-primary, #2563eb); cursor: pointer; padding: 0; }
    .check-row { display: flex; align-items: center; gap: 8px; margin: 8px 0; }
  `]
})
export class GrowthSearchOpsCoworkersComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(false);
  readonly error = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly coworkers = signal<SearchCoworkerDto[]>([]);
  readonly selected = signal<SearchCoworkerDto | null>(null);
  readonly schedule = signal<SearchCoworkerScheduleDto | null>(null);
  readonly runs = signal<SearchCoworkerRunDto[]>([]);

  selectedPropertyId: string | null = null;
  createOpen = false;
  createModel = this.emptyCreate();
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthSearchOpsChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
    void this.setupLiveSubscription();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  propertyQuery(): Record<string, string> {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  propertyOptions(): Array<{ label: string; value: string }> {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  positionOptions(): Array<{ label: string; value: string }> {
    return ['seo.lead', 'seo.technical', 'seo.onpage', 'seo.content', 'seo.offpage', 'seo.local', 'seo.sem', 'seo.analyst']
      .map(code => ({ label: this.t('growth.searchops.positions.' + code), value: code }));
  }

  coverageOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_COVERAGE_MODES.map(value => ({ label: this.t('growth.searchops.coverageMode.' + value), value }));
  }

  autonomyOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_AUTONOMIES.map(value => ({ label: this.t('growth.searchops.autonomy.' + value), value }));
  }

  cadenceOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_CADENCES.filter(item => item !== 'cron').map(value => ({
      label: this.t('growth.searchops.cadence.' + value),
      value
    }));
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { propertyId }, queryParamsHandling: 'merge' });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties.length > 0) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadCoworkers();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  openCreate(): void {
    this.createModel = this.emptyCreate();
    this.createOpen = true;
  }

  saveCoworker(): void {
    if (!this.selectedPropertyId || !this.createModel.name.trim() || !this.createModel.ownerUserId) {
      return;
    }
    this.api.createCoworker({
      searchPropertyId: this.selectedPropertyId,
      name: this.createModel.name,
      ownerUserId: this.createModel.ownerUserId,
      positionCodes: this.createModel.positionCodes,
      coverageMode: this.createModel.coverageMode,
      autonomy: this.createModel.autonomy,
      killSwitch: this.createModel.killSwitch,
      cadence: this.createModel.cadence,
      localTime: this.createModel.localTime,
      weekday: this.createModel.weekday,
      timeZone: 'UTC',
      scheduleEnabled: true
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: created => {
        this.createOpen = false;
        this.loadCoworkers(created.id);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  selectCoworker(row: SearchCoworkerDto): void {
    this.selected.set(row);
    this.loadDetail(row.id);
  }

  pause(row: SearchCoworkerDto): void {
    this.api.pauseCoworker(row.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadCoworkers(row.id),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  resume(row: SearchCoworkerDto): void {
    this.api.resumeCoworker(row.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadCoworkers(row.id),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  runNow(row: SearchCoworkerDto): void {
    this.api.runNow(row.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadCoworkers(row.id),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  private loadCoworkers(selectId?: string): void {
    if (!this.selectedPropertyId) {
      this.coworkers.set([]);
      this.selected.set(null);
      return;
    }
    this.api.listCoworkers(this.selectedPropertyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: items => {
        this.coworkers.set(items);
        const current = items.find(item => item.id === (selectId ?? this.selected()?.id)) ?? items[0] ?? null;
        this.selected.set(current);
        if (current) {
          this.loadDetail(current.id);
        } else {
          this.schedule.set(null);
          this.runs.set([]);
        }
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private loadDetail(id: string): void {
    forkJoin({
      schedules: this.api.listSchedules(id).pipe(catchError(() => of([] as SearchCoworkerScheduleDto[]))),
      runs: this.api.listRuns(id).pipe(catchError(() => of([] as SearchCoworkerRunDto[])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: detail => {
        this.schedule.set(detail.schedules[0] ?? null);
        this.runs.set(detail.runs);
      }
    });
  }

  private emptyCreate() {
    return {
      name: '',
      ownerUserId: '',
      positionCodes: ['seo.lead'],
      coverageMode: 'assist_human',
      autonomy: 'suggest',
      killSwitch: false,
      cadence: 'weekly',
      localTime: '07:00',
      weekday: 1
    };
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
